/**
 * Sentry Configuration for Camp Connect Frontend
 * Provides error tracking and performance monitoring for React app
 */

import * as Sentry from '@sentry/react';
import { replayIntegration } from '@sentry/react';

/**
 * Get git commit SHA for release tracking (build-time)
 */
const getGitCommitSha = () => {
  // This will be replaced at build time by Vite
  return import.meta.env.VITE_GIT_COMMIT_SHA || 'unknown';
};

/**
 * Filter sensitive data from events
 */
const filterSensitiveData = (event) => {
  const sensitiveKeys = [
    'password', 'token', 'secret', 'key', 'authorization', 'cookie',
    'session', 'auth', 'credential', 'api_key', 'access_token', 'refresh_token'
  ];
  
  const filterObject = (obj) => {
    if (typeof obj !== 'object' || obj === null) return obj;
    
    const filtered = Array.isArray(obj) ? [] : {};
    
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase();
      
      if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
        filtered[key] = '[FILTERED]';
      } else if (typeof value === 'object') {
        filtered[key] = filterObject(value);
      } else {
        filtered[key] = value;
      }
    }
    
    return filtered;
  };
  
  // Filter request data
  if (event.request) {
    event.request = filterObject(event.request);
  }
  
  // Filter extra data
  if (event.extra) {
    event.extra = filterObject(event.extra);
  }
  
  // Filter contexts
  if (event.contexts) {
    event.contexts = filterObject(event.contexts);
  }
  
  return event;
};

/**
 * Initialize Sentry for the frontend
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN_FRONTEND;
  const environment = import.meta.env.MODE || 'development';
  const release = import.meta.env.VITE_SENTRY_RELEASE || getGitCommitSha();
  
  // Disable Sentry in development environment
  if (environment === 'development') {
    console.log('🔧 Sentry disabled in development environment');
    return;
  }
  
  if (!dsn) {
    console.warn('⚠️  Sentry DSN not configured. Error tracking disabled.');
    return;
  }

  // Configure integrations
  const integrations = [];
  
  // Add Session Replay for staging (disabled in production to save quota)
  if (environment === 'staging') {
    integrations.push(
      replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true, // Mask sensitive inputs
        maskAllSelectors: [
          '[data-sentry-mask]',
          '.password',
          '.token',
          '.secret',
          '.key'
        ],
      })
    );
  }

  Sentry.init({
    dsn,
    environment,
    release,
    integrations,
    
    // Performance monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    
    // Session Replay sampling
    replaysSessionSampleRate: environment === 'staging' ? 0.1 : 0,
    replaysOnErrorSampleRate: environment === 'staging' ? 1.0 : 0,
    
    // Error filtering
    beforeSend(event, hint) {
      // Filter sensitive data
      event = filterSensitiveData(event);
      
      // Filter out certain errors in development
      if (environment === 'development') {
        // Don't send network errors in development
        if (event.exception?.values?.[0]?.type === 'NetworkError') {
          return null;
        }
        
        // Don't send validation errors in development
        if (event.exception?.values?.[0]?.type === 'ValidationError') {
          return null;
        }
      }
      
      // Add custom tags
      event.tags = {
        ...event.tags,
        component: 'frontend',
        service: 'camp-connect',
      };
      
      return event;
    },
    
    // Custom error grouping
    beforeSendTransaction(event) {
      // Add custom transaction tags
      event.tags = {
        ...event.tags,
        component: 'frontend',
        service: 'camp-connect',
      };
      
      return event;
    },
    
    // Debug mode
    debug: environment === 'development',
    
    // Capture unhandled rejections
    captureUnhandledRejections: true,
    
    // Capture uncaught exceptions
    captureUncaughtException: true,
    
    // Set user context
    initialScope: {
      tags: {
        component: 'frontend',
        service: 'camp-connect',
      },
    },
  });

  console.log('✅ Sentry initialized for frontend');
};

/**
 * Capture custom errors
 */
export const captureError = (error, context = {}) => {
  Sentry.withScope((scope) => {
    // Add context
    Object.keys(context).forEach(key => {
      scope.setContext(key, context[key]);
    });
    
    // Add user information if available
    if (context.user) {
      scope.setUser(context.user);
    }
    
    // Add tags
    if (context.tags) {
      Object.keys(context.tags).forEach(key => {
        scope.setTag(key, context.tags[key]);
      });
    }
    
    Sentry.captureException(error);
  });
};

/**
 * Capture custom messages
 */
export const captureMessage = (message, level = 'info', context = {}) => {
  Sentry.withScope((scope) => {
    // Add context
    Object.keys(context).forEach(key => {
      scope.setContext(key, context[key]);
    });
    
    // Add user information if available
    if (context.user) {
      scope.setUser(context.user);
    }
    
    // Add tags
    if (context.tags) {
      Object.keys(context.tags).forEach(key => {
        scope.setTag(key, context.tags[key]);
      });
    }
    
    Sentry.captureMessage(message, level);
  });
};

/**
 * Start a transaction for performance monitoring
 */
export const startTransaction = (name, op = 'navigation') => {
  return Sentry.startTransaction({
    name,
    op,
    tags: {
      component: 'frontend',
      service: 'camp-connect',
    },
  });
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (message, category = 'custom', level = 'info', data = {}) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};

/**
 * Set user context
 */
export const setUser = (user) => {
  Sentry.setUser(user);
};

/**
 * Set custom context
 */
export const setContext = (key, context) => {
  Sentry.setContext(key, context);
};

/**
 * Set custom tags
 */
export const setTag = (key, value) => {
  Sentry.setTag(key, value);
};

/**
 * Error reporting utility for API calls
 */
export const reportApiError = (error, endpoint, method = 'GET', context = {}) => {
  captureError(error, {
    tags: {
      errorType: 'api',
      endpoint,
      method,
    },
    context: {
      api: {
        endpoint,
        method,
        status: error.status,
        statusText: error.statusText,
      },
      ...context,
    },
  });
};

/**
 * Error reporting utility for user actions
 */
export const reportUserActionError = (error, action, context = {}) => {
  captureError(error, {
    tags: {
      errorType: 'userAction',
      action,
    },
    context: {
      userAction: {
        action,
        timestamp: new Date().toISOString(),
      },
      ...context,
    },
  });
};

/**
 * Performance monitoring utility
 */
export const measurePerformance = (name, fn) => {
  const transaction = startTransaction(name, 'custom');
  
  try {
    const result = fn();
    
    // If it's a promise, wait for it
    if (result && typeof result.then === 'function') {
      return result.finally(() => {
        transaction.finish();
      });
    }
    
    transaction.finish();
    return result;
  } catch (error) {
    transaction.setStatus('internal_error');
    transaction.finish();
    throw error;
  }
};

/**
 * Sentry Error Boundary for React
 * Use this as a higher-order component
 */
export const withSentryErrorBoundary = (Component, fallback) => {
  return Sentry.withErrorBoundary(Component, { fallback });
};

export default Sentry;
